import { Pagination } from "constants/type"

import { AdminActivityUserModel } from "services/activity/type"
import { NgrInputs, Transaction } from "services/total/types"

import { CardGateway, Comments } from "types/api"

export interface IUserService {
  getUser: () => Promise<User | void>
  editUser: (id: string, payload: EditUserPayload) => Promise<EditUserResponse | void>
  editUserBalance: (id: string, payload: EditUserBalancePayload) => Promise<EditUserBalanceResponse[] | void>
  getUserBets: GetUserBets
  getUsersTable: GetUserTableHandler
  getUserReferrals: GetUserReferrals
  getUserTransactions: GetUserTransactions
  getUserRewardGames: GetUserRewardGames
  getUserById: (id: string) => Promise<GetSingleUser | void>
  addUserRewardGames: (id: string, gameIdentifier: any) => Promise<any>
  addComment: (id: string, comment: string, category: string) => Promise<any>

  getUserBonuses: GetUserBonuses
  getAffiliatedUsersTable: GetAffiliateUsersTableHandler
}

export type GetUserTableHandler = (
  page: number,
  pageSize: number,
  id: number | undefined,
  filter: { q: string; createdAt: (Date | null)[]; country: string | null },
  sortRow?: string,
  sortOrder?: string
) => Promise<UsersTableResponse | void>

export type GetUserBets = (
  page: number,
  pageSize: number,
  id: number | undefined,
  filters: any,
  sortRow?: string,
  sortOrder?: string
) => Promise<GetUserBetsResponse | void>
export type GetUserReferrals = (
  page: number,
  pageSize: number,
  id: number | undefined,
  filters: any,
  sortRow?: string,
  sortOrder?: string
) => Promise<GetUserReferralsResponse | void>
export type GetUserTransactions = (
  page: number,
  pageSize: number,
  id: number | undefined,
  filter: { type: string; status: string },
  sortRow?: string,
  sortOrder?: string
) => Promise<GetUserTransactionsResponse | void>
export type GetUserRewardGames = (
  page: number,
  pageSize: number,
  id: number | undefined,
  filters: any,
  sortRow?: string,
  sortOrder?: string
) => Promise<GetUserRewardGamesResponse | void>
export type GetUserBonuses = (
  page: number,
  pageSize: number,
  id: string | undefined,
  filters: any,
  sortRow?: string,
  sortOrder?: string
) => Promise<Paginated<UserBonus[]> | void>

export type User = {
  avatar_url: string | null
  display_name: string | null
  username: string
  email_address: string
  email_verified: boolean
  phone_number: string | null
  phone_number_verified: boolean
  two_fa_code: null | boolean
  use_two_fa: boolean
  created_at: Date
  rakeback_balance: number
  rakeback_earned: number
  discord_name: null | string
  role: string
  wallet_coin: Coin
  referral_links: string[]
  wallets: Wallets
  personal_profile: PersonalProfile
  user_access: UserAccess
  user_settings: UserSettings
  account_policy?: UserAccountPolicy | null
  admin_preference?: AdminPreference | null
  tenant_id?: string
  permissions?: string[]
  effective_role?: { id: string; name: string; is_builtin: boolean }
}

export type AdminPreference = {
  language: string
  timezone: string
}

export type PersonalProfile = {
  id: number
  user_id: number
  first_name: string | null
  last_name: string | null
  dob: string | null
  gender: "Male" | "Female" | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  country: string | null
  custom_halls: GameProvider[]
  misc: PersonalProfileMisc | null
  created_at: Date
  updated_at: Date
}

export type PersonalProfileMisc = {
  dark_streamer?: boolean
  sv_streamer?: boolean
}

export type UserAccountPolicy = {
  user_id: string
  is_c2_streamer: boolean
  is_dark_streamer: boolean
  is_sv_streamer: boolean

  exclude_dashboard_ggr: boolean
  exclude_c2_ggr: boolean
  exclude_dark_ggr: boolean

  disable_withdrawals: boolean
  exclude_from_promotions: boolean

  created_at: Date
  updated_at: Date
}

export type Wallets = {
  id: number
  user_id: number
  coin: Coin
  address: null | string
  balance: string
  used: boolean
  last_deposit_date: null | Date
  created_at: Date
  updated_at: Date
}

export type UserAccess = {
  id: number
  user_id: number
  deposit: boolean
  withdraw: boolean
  games: boolean
  chat: boolean
  chat_ban_expire: null | Date
  created_at: Date
  updated_at: Date
}

export type UserSettings = {
  id: number
  user_id: number
  incognito: boolean
  allow_marketing: boolean
  allow_notifications: boolean
  created_at: Date
  updated_at: Date
}

/**
 * Built-in role ids — string constants that mirror the BE's `BUILTIN_ROLE_IDS`
 * (see `core-api/src/constants/admin.ts`). Custom roles are UUIDs and do NOT
 * appear here. A user's `role` field is now `string` and can be any of these
 * values OR a custom UUID.
 */
export const BUILTIN_ROLE_IDS = {
  ADMIN: "ADMIN",
  OPERATOR: "OPERATOR",
  VIP_SUPPORT: "VIP_SUPPORT",
  SUPPORT: "SUPPORT",
  SUPPORT_PLUS: "SUPPORT_PLUS",
  REFMAN: "REFMAN",
  CRM_MANAGER: "CRM_MANAGER",
  // Not for AP
  INFLUENCER: "INFLUENCER",
  VIP: "VIP",
  USER: "USER",
  // deprecated
  MOD: "MOD"
} as const

export type BuiltinRoleId = (typeof BUILTIN_ROLE_IDS)[keyof typeof BUILTIN_ROLE_IDS]

/**
 * Static lists of admin-panel-eligible roles, used only by the `Admins`
 * filter dropdown and `ModalCreateUser` "create with role" picker. These are
 * NOT authorization gates — runtime access is decided by
 * `PermissionsContext` / the backend `requirePermission` middleware
 */
export const apAccessRolesWithRefman = [
  BUILTIN_ROLE_IDS.ADMIN,
  BUILTIN_ROLE_IDS.OPERATOR,
  BUILTIN_ROLE_IDS.VIP_SUPPORT,
  BUILTIN_ROLE_IDS.SUPPORT,
  BUILTIN_ROLE_IDS.SUPPORT_PLUS,
  BUILTIN_ROLE_IDS.CRM_MANAGER,
  BUILTIN_ROLE_IDS.REFMAN
]
export const adminRolesWithUser = [
  BUILTIN_ROLE_IDS.ADMIN,
  BUILTIN_ROLE_IDS.OPERATOR,
  BUILTIN_ROLE_IDS.VIP_SUPPORT,
  BUILTIN_ROLE_IDS.SUPPORT,
  BUILTIN_ROLE_IDS.SUPPORT_PLUS,
  BUILTIN_ROLE_IDS.CRM_MANAGER,
  BUILTIN_ROLE_IDS.REFMAN,
  BUILTIN_ROLE_IDS.USER
]

/**
 * Roles allowed to create/manage Bonus V2 configurations from the admin UI.
 * Used only to gate the "Create bonus" button; runtime access is still
 * enforced by the backend `requirePermission` middleware.
 */
export const createBonusAccess: string[] = [
  BUILTIN_ROLE_IDS.ADMIN,
  BUILTIN_ROLE_IDS.OPERATOR,
  BUILTIN_ROLE_IDS.VIP_SUPPORT,
  BUILTIN_ROLE_IDS.CRM_MANAGER
]

export enum Coin {
  BTC = "BTC",
  DOGE = "DOGE",
  XRP = "XRP",
  BNB = "BNB",
  MATIC = "MATIC",
  LTC = "LTC",
  TRX = "TRX",

  SHIB = "SHIB",
  SHIB_BSC = "SHIB_BSC",

  ETH = "ETH",
  ETH_BASE = "ETH_BASE",
  ETH_ARBITRUM = "ETH_ARBITRUM",
  ETH_BSC = "ETH_BSC",

  USDT = "USDT",
  USDT_TRX = "USDT_TRX",
  USDT_POLYGON = "USDT_POLYGON",
  USDT_BSC = "USDT_BSC",
  USDT_ARBITRUM = "USDT_ARBITRUM",

  USDC = "USDC",
  USDC_BASE = "USDC_BASE",
  USDC_ARBITRUM = "USDC_ARBITRUM",
  USDC_POLYGON = "USDC_POLYGON",
  USDC_BSC = "USDC_BSC",
  USDC_TRX = "USDC_TRX",

  USD = "USD",
  B_USD = "B_USD",

  EUR = "EUR",
  CAD = "CAD",
  AUD = "AUD",
  PLN = "PLN"
}

/**
 * Game providers that support custom halls
 */
export type GameProvider = "C2" | "DARK"

export type UsersTableResponse = Pagination & {
  data: SingleUserTableModel[]
  // SOFT-554 (T09): on microservice tenants a balance sort re-orders only the current page by the
  // displayed authoritative balances, so the cross-page order is approximate — the FE labels the
  // sort header when this is set. Absent on monolith tenants.
  approximate_order?: boolean
}

export interface UserStatsTableEntry {
  period: "day" | "week" | "fortnight" | "30days" | "overall"
  wagered: number
  profit: number
  // SOFT-555: `null` in microservice mode (no authoritative per-period bonus aggregate yet).
  bonuses: number | null
}

export type KYCFiles = {
  img_idcard_front?: string
  img_idcard_back?: string
  doc_proof_address?: string
  doc_add_verify?: string
  status?: string
  other?: string | { reason?: string }
  created_at?: string
  updated_at?: string
}

export type SingleUserTableModel = {
  id: string
  display_name: string
  username: string
  email_address: string
  phone_number: string
  phone_number_verified: boolean
  discord_name: null | string
  email_verified: boolean
  activated: boolean
  exclusion_end: Date
  exclusion_start: Date
  kyc: boolean
  kyc_files: KYCFiles
  use_two_fa: boolean
  role: string
  level: number
  rakeback_earned: number
  rakeback_balance: number
  trusted_level: number
  trusted_status: string
  bets: []
  created_at: Date
  Affiliate: Affiliate[]
  referral_links: []
  referrals: number
  wins: string
  coins: {
    // `USD` / `B_USD` may be `null` when the balance authority is unavailable
    // (see `balance_unavailable`); crypto coins stay numeric.
    [key in keyof typeof Coin]: number | null
  }
  // SOFT-535 (microservice tenants) — EXACT cash balance in the player's own coin (minor units)
  // + that coin, read FS-native with no FX. `coins.USD` is the rounded USD view; use these for the
  // true native figure. `null` when the balance authority is unavailable.
  cash_native_cents?: number | null
  cash_native_coin?: string
  is_referred: boolean
  affiliate_earned: number
  affiliate_balance: number
  influencer_percent: null | number
  affiliate_percent: number
  eth_balance: string
  ltc_balance: string
  btc_balance: string
  usdt_balance: string
  // In microservice mode, when the balance authority (FS/bonus-service) is
  // unavailable these fields come back `null` (see `balance_unavailable`).
  // `null` means "currently unknown", NOT "$0" — never render it as zero.
  wager: string | null
  usd_balance: string | null
  busd_balance: string | null
  balance: string | null
  // SOFT-554: FS lifetime deposit total (cents). `null` + `financials_unavailable` when the
  // financial-service is down in microservice mode — render a dash, never `$0`.
  total_deposit_amount?: number | null
  // Set when the fail-closed backend nulled wager/wins/total_deposit_amount (financial-service down).
  financials_unavailable?: boolean
  // Set when the fail-closed backend nulled the balance columns (balance authority down).
  balance_unavailable?: boolean
  comments: Comments[]
  next_card_gateway?: CardGateway
  tenant_id?: string
  gameSessions: GameSessions[]
}

export type VipProgress = {
  totalWagerUsd: number
  tierId: string | null
  tierName: string | null
  rakebackPercent: number
  nextTier: {
    id: string
    name: string
    minTotalWagerUsd: number
    rakebackPercent: number
  } | null
  progressToNextTierPercent: number
  remainingToNextTierUsd: number | null
}

export type GameSessions = {
  id: string
  user_id: string
  short_id: string
  created_at: string
  updated_at: string
  currency: string
}

export type UserAffiliate = {
  id: string
  code: string
  commission_mode: AffiliateCommissionMode
  return_percent: number | null
  cpa_config?: AffiliateCpaConfig
  balance: number
  total_earned: number
  estimated_unreleased?: number
  created_at: string

  total_refs?: number
  referral_links?: AffiliateReferralLinkRow[]
}

export type AffiliateReferralLinkRow = {
  id: string
  user_id: string
  username: string
  active: boolean
  deposited: number
  last_deposit_amount: number
  last_deposit_date: string | null
  earned_usd: number
  earned_estimated_usd: number
  created_at: string
}

export type Affiliate = {
  code: string
  influencer_percent: null | number
  balance: number
  total_earned: number
  return_percent: number
  _count: {
    referral_links: number
  }
}

export type GetSingleUser = SingleUserTableModel & {
  wallet_coin: string
  bonus_native_coin?: string
  effective_role?: { id: string; name: string; is_builtin: boolean }
  // Balance authority (FS/bonus-service) down in microservice mode → balance
  // fields (`balance`, `usd_balance`, `busd_balance`, `coins.USD/B_USD`) are
  // `null`. Render "unavailable", not "$0". The rest of the page still loads.
  balance_unavailable?: boolean
  // FS/bonus-service down → the financial totals below are `null`. Show a
  // placeholder, NEVER render `null` as `0` (would misreport PNL/deposits).
  // `total_ngr` / `ngrInputs` / `freespinWinnings` stay MNT-derived (untouched).
  financials_unavailable?: boolean
  total_deposit_amount: number | null
  total_withdrawal_amount: number | null
  total_pnl: number | null
  total_reward_amount: number | null
  total_wager: number | null
  total_win: number | null
  total_bets: string | null
  total_ggr: number | null
  total_ngr: number
  ngrInputs: NgrInputs
  freespinWinnings: number
  personal_profile: PersonalProfile
  account_policy?: UserAccountPolicy | null
  last30DaysWagered: number | null
  last30DaysDeposits: number | null
  last30DaysPNL: number | null
  last7DaysPNL: number | null
  site_lang: string
  vipLabel: string
  vipProgress: VipProgress | null
  affiliates: UserAffiliate[]
  signup_ip: string | null
  signup_at?: string | Date | null
  last_login_ip?: string | null
  last_login_at?: string | Date | null
  stag: string | null
  referred_by: {
    user_id: string
    username: string
    affiliate_code: string
  } | null
}
export type UserTransaction = Omit<Transaction, "updated_at" | "user_id" | "user" | "withdrawal_address" | "payout_id">

export type Paginated<T> = Pagination & { data: T }
export type GetUserTransactionsResponse = Pagination & {
  data: UserTransaction[]
}

export type GetUserBetsResponse = Pagination & {
  data: any
}

export type GetUserReferralsResponse = Pagination & {
  data: any
}

export type GetUserRewardGamesResponse = Pagination & {
  data: any
}

export type GetUserCommentsResponse = Pagination & {
  data: any
}

export type EditUserResponse = AdminActivityUserModel

export type EditUserPayload = {
  username: string
  first_name: string
  last_name: string
  display_name?: string
  email_address: string
  phone_number: string
  dob: string | null
  // activated: boolean
  email_verified: boolean
  role_id?: string
  country: string
  vipTierId?: string
  rakeback_balance: Number
  exclusion_period?: string
  stag?: string
  affiliate_code?: string
}

export type EditUserBalanceResponse = {
  address: null | string
  balance: string
  coin: string
  created_at: string | Date
  id: number
  last_deposit_date: null | string | Date
  updated_at: string
  used: boolean
  user_id: number
}

export type EditUserBalancePayload = {
  BTC: number
  USDT: number
  ETH: number
  LTC: number
  USD: number
  B_USD: number
  // SOFT-535 (microservice tenants only) — the currency the USD cash amount is entered in. The
  // backend converts it to the USD cash wallet (FX). Omitted by monolith tenants → backend treats
  // the amount as USD, exactly as before. Never applies to B_USD (bonus balance).
  amountCurrency?: string
}

export type UpdateUserAffiliatePayload = {
  commission_mode?: AffiliateCommissionMode
  return_percent?: number
  cpa_config?: AffiliateCpaConfig | null
}

export type UpdateUserAffiliatesBulkPayload = {
  commission_mode?: AffiliateCommissionMode
  return_percent?: number
  cpa_config?: AffiliateCpaConfig | null
}

export type AffiliateCpaConfig = {
  enabled?: boolean
  reward_cents: number

  require_kyc?: boolean | null

  min_total_deposit_cents?: number | null
  min_total_wager_cents?: number | null
}

export enum AffiliateCommissionMode {
  RAKE = "RAKE",
  NGR = "NGR",
  CPA = "CPA"
}

export enum BonusStatus {
  ACTIVE = "ACTIVE",
  PENDING = "PENDING",
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  TRANSFERRED = "TRANSFERRED"
}

export type UserBonus = {
  id: string
  bonus_id: string
  user_id: string
  bonus_amount: number
  wagered_amount: number
  wager_requirements: number
  transferred_amount: number
  deposit_amount?: number
  expires_at: string
  status: BonusStatus
  created_at: string
  updated_at: string
}

export enum ReloadStatus {
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED"
}

export type UserReload = {
  id: string
  user_id: string
  total_amount: number
  days: number
  daily_amount: number
  started_at: string | null
  expires_at: string | null
  last_claimed_at: string | null
  claims_made: number
  status: ReloadStatus
  created_at: string
  updated_at: string
  assigned_by: string | null
}

export type GetAffiliateUsersTableHandler = (
  page: number,
  pageSize: number,
  id: string,
  filter: { q: string; createdAt: (Date | null)[] } | undefined,
  sortRow?: string,
  sortOrder?: string
) => Promise<UsersTableResponse | void>
